import { ArrowRight, CheckCircle2, ChevronDown, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import { usePageTitle } from '@/hooks/use-document-title'
import { SPRINT_DEFINITION } from '@/lib/marketing-content'
import Footer from './footer'

const steps = [
  {
    title: 'Create an account',
    description:
      'Sign up free and start practicing right away — the problem platform and courses are yours to use while you decide whether coaching is right for you.',
  },
  {
    title: 'Intro call & audition',
    description:
      'Before we take you on as a coaching client, we talk. We look at where you are, where you want to be, and whether we honestly believe coaching will change your outcome. We do not work with everyone.',
  },
  {
    title: 'Kickoff',
    description:
      'You and your coach set a concrete goal, agree on how progress will be measured, and plan your first sprint.',
  },
  {
    title: 'Sprints',
    description:
      'The program runs in sprints — focused blocks of coaching, practice, and mock interviews, each aimed at one goal. After each sprint you get an honest assessment and together you decide what comes next.',
  },
]

const weProvide = [
  '1:1 coaching from experienced interviewers',
  'Structured sprints with a clear goal and honest progress assessments',
  'Mock interviews with specific, actionable feedback',
  'Full access to the practice platform: 26 topics, ~300 curated problems',
  'Accountability — a coach who tracks your progress between sessions',
]

const weDoNotProvide = [
  'Job placement, referrals, or guaranteed offers — we prepare you; you interview',
  'Leaked or proprietary interview questions from real companies',
  'Shortcuts — we will not help you misrepresent your abilities',
  'Coaching for everyone — if we do not believe we can help you, we say so',
]

interface FaqEntry {
  question: string
  answer: ReactNode
}

const faqs: FaqEntry[] = [
  {
    question: 'How does onboarding work? What is the audition?',
    answer: (
      <>
        <p className="mb-3">
          Onboarding starts with a free account and an intro call. Before we
          take anyone on as a coaching client, we run a short audition — a
          working session where we see how you approach problems, take feedback,
          and communicate under pressure.
        </p>
        <p>
          The audition is not about being good at interviews already — that is
          what the program is for. It is about whether coaching will genuinely
          change your outcome, and whether we are the right people to deliver
          it.
        </p>
      </>
    ),
  },
  {
    question:
      'Under what circumstances does Beyond CTCI decide not to work with an engineer?',
    answer: (
      <>
        <p className="mb-3">
          We decline engagements when we honestly believe coaching will not
          change the outcome. Common reasons: the timeline is too short for the
          gap we would need to close, the fundamentals are not yet in place for
          coaching to add value over self-study, or someone is looking for
          shortcuts rather than preparation.
        </p>
        <p>
          When we decline, we say why, and we point you at what we think you
          should do instead — usually a self-study path on the free platform.
        </p>
      </>
    ),
  },
  {
    question: 'Do I need the book to use the platform?',
    answer: (
      <p>
        No. The platform is a companion to{' '}
        <em>Beyond Cracking the Coding Interview</em> and follows its topics,
        but every problem, course, and editorial stands on its own.
      </p>
    ),
  },
  {
    question: 'How much does coaching cost?',
    answer: (
      <p>
        The practice platform is free. Coaching pricing depends on your goal and
        timeline and is discussed on your intro call — see the{' '}
        <Link
          href="/pricing"
          className="text-primary underline underline-offset-4 hover:opacity-80"
        >
          pricing page
        </Link>{' '}
        for how the model works.
      </p>
    ),
  },
]

export default function HowItWorks() {
  usePageTitle('How the Program Works')

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col">
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <h1 className="mb-4 text-4xl font-bold tracking-tight">
          How the <span className="text-[#51B0BC]">Program</span> Works
        </h1>
        <p className="text-muted-foreground mb-12 max-w-3xl text-lg leading-relaxed">
          A guided path from where you are now to interview-ready — built on
          sprints, honest feedback, and a coach who is accountable to your goal.
        </p>

        <section className="mb-14">
          <ol className="space-y-6">
            {steps.map((step, index) => (
              <li
                key={step.title}
                className="bg-card flex gap-5 rounded-xl border p-6"
              >
                <div className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-semibold">
                  {index + 1}
                </div>
                <div>
                  <h2 className="mb-1 text-lg font-semibold">{step.title}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="border-l-primary bg-muted/40 mb-14 rounded-r-xl border border-l-4 p-6">
          <h2 className="mb-2 text-xl font-semibold">What is a Sprint?</h2>
          <p className="text-muted-foreground leading-relaxed">
            {SPRINT_DEFINITION}
          </p>
        </section>

        <section className="mb-14 grid gap-6 md:grid-cols-2">
          <div className="bg-card rounded-xl border p-6">
            <h2 className="mb-4 text-xl font-semibold">What We Provide</h2>
            <ul className="space-y-3">
              {weProvide.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-muted-foreground leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-card rounded-xl border p-6">
            <h2 className="mb-4 text-xl font-semibold">
              What We Don&rsquo;t Provide
            </h2>
            <ul className="space-y-3">
              {weDoNotProvide.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <XCircle className="text-destructive mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-muted-foreground leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="faq" className="mb-14 scroll-mt-20">
          <h2 className="mb-6 text-2xl font-semibold">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <details
                key={faq.question}
                className="group bg-card rounded-xl border"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-6 font-semibold [&::-webkit-details-marker]:hidden">
                  {faq.question}
                  <ChevronDown className="text-muted-foreground h-5 w-5 shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <div className="text-muted-foreground px-6 pb-6 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="bg-muted/40 rounded-xl border p-8 text-center">
          <h2 className="mb-2 text-2xl font-semibold">
            Ready for the details?
          </h2>
          <p className="text-muted-foreground mb-6">
            See what the program costs and how to get started.
          </p>
          <Button asChild size="lg" className="group text-base">
            <Link href="/pricing">
              See Pricing
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Button>
        </section>
      </div>
      <Footer />
    </div>
  )
}
